import { getData, navigateV2 } from './router';
import { API_SERVER_URL, logOutFunction, SITE_URL } from './utils';
import { zendoStorage } from '../../hybrid/wrappers';
import memoize from 'lodash.memoize';
import values from 'lodash.values';
import axios from 'axios/index';
import moment from 'moment';
import { emptyLangsData } from './api';

export const getMemoizedData = memoize(
    async (url, userToken, lang, currency, additionalHeaders) => {
        let key = Object.keys(getMemoizedData.cache['__data__']['string']['__data__']).find((key) => {
            let array = key.split('_');
            return array[0] === url && array[1] === userToken && array[2] === lang && array[3] === currency;
        });
        if (key) {
            getMemoizedData.cache.delete(key);
        }
        if (!url) {
            return false;
        }
        const resultUrl = url.includes('https://') || url.includes('http://') ? url : `${API_SERVER_URL}${url}`;
        try {
            const res = await fetch(resultUrl, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json;charset=UTF-8',
                    ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
                    ...(lang ? { 'Accept-Language': lang } : {}),
                    mode: 'no-cors',
                    ...(currency ? { 'Accept-Currency': currency } : {}),
                    ...additionalHeaders
                }
            });
            if (res.status === 500) {
                console.warn(res);
                return { status: 500 };
            }
            if (res.status === 404 || !res) {
                return false;
            } else if (res.status === 503) {
                navigateV2('/error/503');
                return false;
            } else if (res.status === 401) {
                await logOutFunction();
                return false;
            } else {
                return await res.json();
            }
        } catch (e) {
            console.warn(url, e);
            return false;
        }
    },
    (...args) => [...values(args), moment().format('DD.MM.YYYY.hh.mm')].join('_')
);

export const checkIsPartner = memoize(
    async (userToken, lang) => {
        checkIsPartner.cache.clear();
        try {
            return await axios({
                method: 'GET',
                url: `${API_SERVER_URL}/api/v1/user/partner`,
                headers: {
                    ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json;charset=UTF-8',
                    ...(lang ? { 'Accept-Language': lang } : {})
                }
            });
        } catch (e) {
            return e?.response?.status;
        }
    },
    (...args) => [...values(args), moment().format('DD.MM.YYYY.hh.mm').slice(0, -1)].join('_')
);

export const getUserRoles = memoize(
    async (userToken, lang) => {
        getUserRoles.cache.clear();
        try {
            const res = await fetch(`${API_SERVER_URL}/api/v1/user/roles`, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json;charset=UTF-8',
                    ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
                    ...(lang ? { 'Accept-Language': lang } : {}),
                    mode: 'no-cors'
                }
            });
            if ([500, 404, 403, 401].includes(res.status) || !res) {
                return { status: res.status };
            } else if (res.status === 503) {
                navigateV2('/error/503');
                return { status: res.status };
            } else {
                return await res.json();
            }
        } catch (e) {
            return { status: 500 };
        }
    },
    (...args) => [...values(args), moment().format('DD.MM.YYYY.hh').slice(0, -1)].join('_')
);

// Данные о пользователе - очистка
export const clearUserCache = () => {
    getUserRoles.cache.clear();
    checkIsPartner.cache.clear();
    getMemoizedData.cache.clear();
};

// Получение данных из таблицы settings
export const getSettings = memoize(async (alias) => {
    const res = await getData(`${SITE_URL}/api/settings/${alias}`);
    return res?.data?.hasOwnProperty('data') ? res?.data?.data : res?.data;
});

// Получение данных из таблицы settings
export const getDisplaySettings = memoize(async (lang) => {
    const settings = await getData(`${API_SERVER_URL}/api/v1/settings/view`, false, lang);
    return settings?.data || {};
});

// Возвращает false - если пользователь согласился использовать cookie или согласие не требуется
export const getDataCookies = memoize(async () => {
    let agree = zendoStorage.get('cookieAgreement');
    if (agree) {
        return false;
    }
    const res = await getSettings('cookie');
    if (res?.notification === '1') {
        return res;
    }
    return false;
});

export const getGeoName = memoize(
    async (id, lang) => await getData(`/api/v1/geonames/${id}`, false, lang),
    (...args) => values(args).join('_')
);

// Получение списка активных языков
export const getLangs = memoize(async () => {
    const res = await getData(`${SITE_URL}/api/languages?type=active`);
    return res?.data || emptyLangsData;
});

// Получение переводов
export const getLangLocale = memoize(
    async (language, page, group, name) => {
        let lang = language;
        if (!lang) {
            const langFetchRes = await getLangs();
            lang = langFetchRes?.data?.find((item) => item.isDefault)
                ? langFetchRes.data.find((item) => item.isDefault).alias
                : 'ru';
        }
        try {
            const trans = await fetch(
                `${SITE_URL}/api/languages/translations?lang=${lang}${page ? '&page=' + page : ''}${
                    group ? '&group=' + group : ''
                }${name ? '&name=' + name : ''}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            return await trans.json();
        } catch {
            return {};
        }
    },
    (...args) => values(args).join('_')
);

// Получение подкатегорий в магазине
export const getExtraCategoriesTree = memoize(
    async (link, userToken, lang) => {
        let res = await getData(link, userToken, lang);
        return { data: res?.data, nextPage: res?.links?.next };
    },
    (...args) => values(args).join('_')
);

// Список активных валют
export const getCurrencies = memoize(
    async (lang, userToken) => await getData('/api/v1/user/finance/active-real-currency-list', userToken, lang),
    (...args) => [...values(args)].join('_')
);

// Проверка пользователя на право быть спонсором
export const getIsSponsor = memoize(
    async (userToken, refer) => await getData(`/api/v1/sponsors/${refer}`, userToken),
    (...args) => [...values(args)].join('_')
);

// Menu
export const getMenu = memoize(async (location) => {
    const data = await getData(`${SITE_URL}/api/menu?activity=true&location=${location}`);
    return { [location]: data?.data };
});

// Информация о пользователях
export const userInfo = memoize(
    async function (rowData, userToken, lang, setUserData, addOnlyRank = false) {
        try {
            let key = Object.keys(userInfo.cache['__data__']['string']['__data__']).find((key) => {
                let array = key.split('_');
                return (
                    array[0] === rowData?.links?.marketing_profile &&
                    array[1] === rowData?.id &&
                    array[2] === rowData?.links?.partner &&
                    array[3] === lang
                );
            });
            if (key) {
                userInfo.cache.delete(key);
            }
        } catch (e) {}

        let data = {};
        if (!addOnlyRank) {
            data = await getData(
                rowData?.links?.partner || rowData?.links?.marketing_profile || `/api/v1/user/marketing/partners/${rowData.id}`,
                userToken,
                lang
            );
            setUserData({
                ...rowData,
                ...data.data,
                loaded: false
            });
        }
        const rankData = await getData(
            data?.data?.links?.rank_progress ||
                data?.data?.marketing_profile?.links?.rank_progress ||
                `/api/v1/user/marketing/partners/${rowData.id}/rank-progress`,
            userToken,
            lang
        );
        return {
            ...rowData,
            ...data.data,
            rankData: { ...rankData?.data, loaded: true },
            loaded: true
        };
    },
    (rowData, userToken, lang) =>
        `${rowData?.links?.marketing_profile}_${rowData?.id}_${rowData?.links?.partner}_${lang}_${userToken}`
);

export const getWishList = memoize(
    async (userToken, lang, currency) => {
        const res = await getData('/api/v1/shop/wishlist/product-list', userToken, lang, null, currency);

        return res || {};
    },
    (...args) => values(args).join('_')
);
